// ============================================================================
// Portal Aktenwächter — Frontend-Mirror der Repo-Checklisten (Phase 1)
// ----------------------------------------------------------------------------
// Quelle der Wahrheit: `data/portal/akten-templates/*.checklist.json`
// Spiegel analog `contractTemplates.ts` — build-sicher ohne JSON-Import.
//
// SYNC-PFlicht: Änderungen an den JSON-Dateien hier nachziehen.
// ============================================================================

export type AktenChecklistItemStatus = 'open' | 'done' | 'waived';

export interface AktenChecklistItemDef {
  id: string;
  label: string;
  required: boolean;
  order: number;
}

export interface AktenChecklistTemplate {
  template_id: string;
  matter_type: string;
  title: string;
  description?: string;
  version: string;
  items: AktenChecklistItemDef[];
}

export interface AktenChecklistItemState {
  item_id: string;
  status: AktenChecklistItemStatus;
  note?: string;
  completed_at?: string;
  completed_by?: string;
}

/** Spiegel von data/portal/akten-templates/vertrag-beratung.checklist.json */
export const VERTRAG_BERATUNG_CHECKLIST: AktenChecklistTemplate = {
  template_id: 'vertrag-beratung',
  matter_type: 'vertrag_beratung',
  title: 'Vertrag / Beratung — Aktenwächter',
  description: 'Standard-Checkliste für Vertragsprüfung und Beratungsmandate.',
  version: '1.0.0',
  items: [
    { id: 'mandant_identifiziert', label: 'Mandant identifiziert (Name, UID falls Unternehmen)', required: true, order: 1 },
    { id: 'gegenseite_erfasst', label: 'Vertragspartner/Gegenseite erfasst', required: true, order: 2 },
    { id: 'interessenkollision', label: 'Interessenkollisionsprüfung durchgeführt', required: true, order: 3 },
    { id: 'vollmacht_mandat', label: 'Vollmacht / Mandatsverhältnis dokumentiert', required: true, order: 4 },
    { id: 'sachverhalt_strukturiert', label: 'Sachverhalt strukturiert (Ziel, Streitgegenstand)', required: true, order: 5 },
    { id: 'vertragstyp_bestimmt', label: 'Vertragstyp bestimmt (NDA, Dienstleistung, Miete, …)', required: true, order: 6 },
    { id: 'hauptvertrag_abgelegt', label: 'Hauptvertrag im Akt abgelegt', required: true, order: 7 },
    { id: 'anlagen_vollstaendig', label: 'Anlagen/AVV/SLA vollständig', required: false, order: 8 },
    { id: 'klauselpruefung', label: 'Klauselprüfung (Haftung, Laufzeit, Kündigung, Gerichtsstand) abgeschlossen', required: true, order: 9 },
    { id: 'datenschutz_avv', label: 'Datenschutz/AVV geprüft (falls personenbezogene Daten)', required: false, order: 10 },
    { id: 'normen_verknuepft', label: 'Relevante Normen/Judikatur verknüpft', required: false, order: 11 },
    { id: 'beratungsergebnis', label: 'Beratungsergebnis / Handlungsempfehlung dokumentiert', required: true, order: 12 },
    { id: 'naechster_schritt', label: 'Nächster Schritt definiert (Entwurf, Verhandlung, keine Mandatierung)', required: true, order: 13 },
  ],
};

export const AKTEN_CHECKLIST_TEMPLATES: AktenChecklistTemplate[] = [VERTRAG_BERATUNG_CHECKLIST];

export function getAktenChecklistTemplate(templateId: string): AktenChecklistTemplate | undefined {
  return AKTEN_CHECKLIST_TEMPLATES.find((t) => t.template_id === templateId);
}

export function getAktenChecklistTemplateByMatterType(matterType: string): AktenChecklistTemplate | undefined {
  return AKTEN_CHECKLIST_TEMPLATES.find((t) => t.matter_type === matterType);
}

/** Initiale Checklisten-States aus Template (alle Punkte offen). */
export function buildInitialChecklistState(template: AktenChecklistTemplate): AktenChecklistItemState[] {
  return [...template.items]
    .sort((a, b) => a.order - b.order)
    .map((item) => ({ item_id: item.id, status: 'open' as const }));
}

export interface AktenGuardProgress {
  requiredTotal: number;
  requiredDone: number;
  optionalTotal: number;
  optionalDone: number;
  allRequiredDone: boolean;
}

/** Fortschritt für Aktenwächter-Banner (nur Pflichtpunkte entscheiden über „fertig“). */
export function computeAktenGuardProgress(
  template: AktenChecklistTemplate,
  states: AktenChecklistItemState[],
): AktenGuardProgress {
  const stateMap = new Map(states.map((s) => [s.item_id, s]));
  let requiredTotal = 0;
  let requiredDone = 0;
  let optionalTotal = 0;
  let optionalDone = 0;

  for (const item of template.items) {
    const st = stateMap.get(item.id);
    const done = st?.status === 'done' || st?.status === 'waived';
    if (item.required) {
      requiredTotal += 1;
      if (done) requiredDone += 1;
    } else {
      optionalTotal += 1;
      if (done) optionalDone += 1;
    }
  }

  return {
    requiredTotal,
    requiredDone,
    optionalTotal,
    optionalDone,
    allRequiredDone: requiredTotal > 0 && requiredDone === requiredTotal,
  };
}
